import { REQUIRED_MESSAGE } from "@/ds";
import type {
  CriarEscopoMacroRequest,
  DestinoPacote,
  FonteEscopo,
  TipoManutencao,
} from "@/modules/financeiro-obras";
import {
  DESCRICAO_LIMITE,
  DESCRICAO_MAX,
  ESCOPO_TEXTO_LIMITE,
  ESCOPO_TEXTO_MAX,
  FIM_ANTES_DO_INICIO,
  FORA_DO_PERIODO,
} from "./adicionar-escopo-macro-dialog.constants";

export type Alternativa = "PADRAO" | "PERSONALIZADO";

export type FormaFormulario = "TEXTO" | "CATALOGO" | "EQUIPAMENTO";

export interface Rascunho {
  nome: string;
  escopoMacroId: string;
  grupo: string;
  equipamentoSfiId: string;
  tipoManutencao: TipoManutencao | "";
  inicio: Date | null;
  fim: Date | null;
  origemId: string;
  descricao: string;
}

export type CampoForm = keyof Rascunho;
export type ErrosForm = Partial<Record<CampoForm, string>>;

export const RASCUNHO_VAZIO: Rascunho = {
  nome: "",
  escopoMacroId: "",
  grupo: "",
  equipamentoSfiId: "",
  tipoManutencao: "",
  inicio: null,
  fim: null,
  origemId: "",
  descricao: "",
};

export type DestinoEscopo = { obraTipoObraId: string } | DestinoPacote;

export interface PeriodoObra {
  inicio: string | null;
  fim: string | null;
}

export function formaDoFormulario(fonte: FonteEscopo, alternativa: Alternativa): FormaFormulario {
  if (alternativa === "PERSONALIZADO" || fonte === "LIVRE") return "TEXTO";
  return fonte;
}

export const temAlternancia = (fonte: FonteEscopo) => fonte !== "LIVRE";

export function paraIso(data: Date): string {
  const dois = (valor: number) => String(valor).padStart(2, "0");
  return `${data.getFullYear()}-${dois(data.getMonth() + 1)}-${dois(data.getDate())}`;
}

const soData = (iso: string) => iso.slice(0, 10);

function erroDeData(valor: Date | null, periodo: PeriodoObra): string | null {
  if (!valor) return REQUIRED_MESSAGE;
  const iso = paraIso(valor);
  if (periodo.inicio && iso < soData(periodo.inicio)) return FORA_DO_PERIODO;
  if (periodo.fim && iso > soData(periodo.fim)) return FORA_DO_PERIODO;
  return null;
}

export function validarRascunho(
  rascunho: Rascunho,
  forma: FormaFormulario,
  periodo: PeriodoObra,
): ErrosForm {
  const erros: ErrosForm = {};

  if (forma === "TEXTO") {
    if (rascunho.nome.trim() === "") erros.nome = REQUIRED_MESSAGE;
    else if (rascunho.nome.trim().length > ESCOPO_TEXTO_MAX) erros.nome = ESCOPO_TEXTO_LIMITE;
  }
  if (forma === "CATALOGO" && rascunho.escopoMacroId === "") erros.escopoMacroId = REQUIRED_MESSAGE;
  if (forma === "EQUIPAMENTO") {
    if (rascunho.grupo === "") erros.grupo = REQUIRED_MESSAGE;
    if (rascunho.equipamentoSfiId === "") erros.equipamentoSfiId = REQUIRED_MESSAGE;
    if (rascunho.tipoManutencao === "") erros.tipoManutencao = REQUIRED_MESSAGE;
  }

  const erroInicio = erroDeData(rascunho.inicio, periodo);
  const erroFim = erroDeData(rascunho.fim, periodo);
  if (erroInicio) erros.inicio = erroInicio;
  if (erroFim) erros.fim = erroFim;
  if (
    !erroInicio &&
    !erroFim &&
    rascunho.inicio &&
    rascunho.fim &&
    paraIso(rascunho.fim) < paraIso(rascunho.inicio)
  )
    erros.fim = FIM_ANTES_DO_INICIO;

  if (rascunho.origemId === "") erros.origemId = REQUIRED_MESSAGE;
  if (rascunho.descricao.length > DESCRICAO_MAX) erros.descricao = DESCRICAO_LIMITE;

  return erros;
}

export function montarRequest(
  destino: DestinoEscopo,
  forma: FormaFormulario,
  alternativa: Alternativa,
  rascunho: Rascunho,
): CriarEscopoMacroRequest | null {
  if (!rascunho.inicio || !rascunho.fim) return null;
  const base = {
    ...destino,
    inicio: paraIso(rascunho.inicio),
    fim: paraIso(rascunho.fim),
    origemId: rascunho.origemId,
    ...(rascunho.descricao.trim() ? { descricao: rascunho.descricao.trim() } : {}),
  };

  if (forma === "TEXTO") return { ...base, forma: "PERSONALIZADO", escopo: rascunho.nome.trim() };
  if (alternativa !== "PADRAO") return null;
  if (forma === "CATALOGO")
    return { ...base, forma: "PADRAO", escopoMacroId: rascunho.escopoMacroId };
  if (rascunho.tipoManutencao === "") return null;
  return {
    ...base,
    forma: "PADRAO",
    equipamentoSfiId: rascunho.equipamentoSfiId,
    tipoManutencao: rascunho.tipoManutencao,
    grupo: rascunho.grupo,
  };
}

export function rascunhoPreenchido(rascunho: Rascunho): boolean {
  return JSON.stringify(rascunho) !== JSON.stringify(RASCUNHO_VAZIO);
}

export function comunsPreenchidos({ inicio, fim, origemId, descricao }: Rascunho): boolean {
  return inicio !== null || fim !== null || origemId !== "" || descricao !== "";
}

const COMUNS_VAZIOS = { inicio: null, fim: null, origemId: "", descricao: "" };

export function limparAPartirDe(rascunho: Rascunho, campo: "grupo" | "equipamentoSfiId"): Rascunho {
  if (campo === "grupo")
    return { ...rascunho, grupo: "", equipamentoSfiId: "", tipoManutencao: "", ...COMUNS_VAZIOS };
  return { ...rascunho, equipamentoSfiId: "", tipoManutencao: "", ...COMUNS_VAZIOS };
}
